import AuthLanguageSelect from '../components/AuthLanguageSelect';
import { useState } from 'react';
import { authText } from '../locales/authTranslations';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import BriefyCharacter from '../components/BriefyCharacter';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { currencies } from '../locales/translations';

// Briefcase Logo Component - Using Image
const BriefcaseLogo = ({ size = 80, className = "" }) => (
  <img 
    src="/briefcase-logo.png" 
    alt="BRIEFCASE" 
    width={size} 
    height={size}
    className={className}
    style={{ objectFit: 'contain' }}
  />
);

export { BriefcaseLogo };

export default function AuthPage() {
  const location=useLocation();
  const [isLogin, setIsLogin] = useState(location.pathname!=='/register');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [language, setLanguageValue] = useState('es');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [shake, setShake] = useState(false); // triggers shake + red badge on auth error
  // 2FA challenge state
  const [twoFA, setTwoFA] = useState({ active: false, temp_token: '', code: '' });
  
  const { login, verify2FALogin, register } = useAuth();
  const { t, language: uiLanguage, supportedLanguages } = useLanguage();
  const tx = text => authText(uiLanguage, text);
  const navigate = useNavigate();

  // Trigger shake animation whenever a new error appears
  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 1500);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        const result = await login(email, password);
        if (result && result.requires_2fa) {
          setTwoFA({ active: true, temp_token: result.temp_token, code: '' });
          setLoading(false);
          return;
        }
        navigate('/dashboard', { replace: true });
      } else {
        await register(email, password, businessName, currency, language);
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      let errorMsg = tx("Error al iniciar sesión. Verifica tus datos.");
      
      if (err.response?.data?.detail) {
        errorMsg = typeof err.response.data.detail==='string'?err.response.data.detail:tx("No se pudo completar el acceso. Revisa los campos e inténtalo de nuevo.");
      } else if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
        errorMsg = tx("Error de conexión. Por favor verifica tu internet e intenta de nuevo.");
      } else if (err.code === 'ECONNABORTED') {
        errorMsg = tx("Tiempo de espera agotado. Intenta de nuevo.");
      } else if (err.message) {
        errorMsg = err.message;
      }
      
      setError(errorMsg);
      triggerShake();
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verify2FALogin(twoFA.temp_token, twoFA.code);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err.message || tx("Código 2FA inválido"));
      triggerShake();
    } finally {
      setLoading(false);
    }
  };

  return <main className="bc-public bc-auth" data-i18n-skip="true"><section className="bc-auth-brand"><Link to="/"><BriefcaseLogo size={44}/>BRIEFCASE</Link><div><small>{tx("TU NEGOCIO, CONECTADO")}</small><h1>{tx("Más claridad. Más posibilidades.")}</h1><p>{tx("Organiza tu negocio en un solo lugar, con BRIEFY a tu lado.")}</p><div className="bc-auth-features">{[tx("Ventas"),tx("Finanzas"),tx("Clientes"),tx("Documentos")].map(item=><span key={item}>{item}</span>)}</div></div><div className="bc-auth-briefy"><BriefyCharacter pose="base" height="100%" blink/></div><small>{tx("Suite de gestión empresarial")}</small></section><section className="bc-auth-form-wrap"><div className="bc-auth-card"><AuthLanguageSelect/><Link to="/" className="bc-auth-mobile-brand"><BriefcaseLogo size={38}/>BRIEFCASE</Link><span className="bc-auth-eyebrow">{tx("BIENVENIDO A TU ESPACIO")}</span><h2>{twoFA.active?tx("Verifica tu acceso"):isLogin?tx("Qué bueno verte."):tx("Comienza con BRIEFCASE")}</h2><p>{twoFA.active?tx("Usa tu aplicación autenticadora o un código de respaldo."):isLogin?tx("Ingresa para continuar con tu negocio."):tx("Crea tu cuenta y organiza lo que viene.")}</p><form className="bc-auth-form" onSubmit={twoFA.active?handleVerify2FA:handleSubmit}>{error&&<div className="bc-auth-error" role="alert">{error}</div>}{twoFA.active?<><label>{tx("Código de verificación")}<input type="text" autoComplete="one-time-code" value={twoFA.code} onChange={e=>setTwoFA({...twoFA,code:e.target.value.replace(/[^A-Za-z0-9]/g,'').toUpperCase()})} maxLength={8} placeholder="000000" autoFocus data-testid="2fa-login-input"/></label><button className="bc-auth-primary" type="submit" disabled={loading||twoFA.code.length<6} data-testid="2fa-login-verify-btn">{loading?tx("Verificando…"):tx("Verificar")}</button><button type="button" onClick={()=>{setTwoFA({active:false,temp_token:'',code:''});setError('');}}>{tx("Cancelar y volver")}</button></>:<><label htmlFor="bc-auth-email">{tx("Correo electrónico")}<input id="bc-auth-email" name="email" type="email" autoComplete="username" value={email} onChange={e=>setEmail(e.target.value)} placeholder="email@empresa.com" required data-testid="email-input"/></label><label htmlFor="bc-auth-password">{tx("Contraseña")}<input id="bc-auth-password" name="password" type="password" autoComplete={isLogin?'current-password':'new-password'} value={password} onChange={e=>setPassword(e.target.value)} placeholder="••••••••" required data-testid="password-input"/></label>{!isLogin&&<><label htmlFor="bc-auth-business">{tx("Nombre del negocio")}<input id="bc-auth-business" type="text" autoComplete="organization" value={businessName} onChange={e=>setBusinessName(e.target.value)} placeholder={tx("Mi empresa")} required data-testid="business-name-input"/></label><div className="bc-auth-two"><label>{t('settings.currency','Moneda')}<select value={currency} onChange={e=>setCurrency(e.target.value)} data-testid="currency-select">{currencies.map(c=><option key={c.code} value={c.code}>{c.code} ({c.symbol})</option>)}</select></label><label>{t('settings.language','Idioma')}<select value={language} onChange={e=>setLanguageValue(e.target.value)} data-testid="language-select">{supportedLanguages.map(l=><option key={l.code} value={l.code}>{l.name}</option>)}</select></label></div></>}<button className="bc-auth-primary" type="submit" disabled={loading} data-testid="submit-button">{loading?t('common.loading','Cargando…'):isLogin?tx("Ingresar"):tx("Crear cuenta")}</button>{isLogin&&<Link className="bc-auth-forgot" to="/reset-password">{tx("¿Olvidaste tu contraseña?")}</Link>}<div className="bc-auth-divider">{tx("o")}</div><button className="bc-auth-google" type="button" data-testid="google-login-btn" onClick={()=>{const redirectUrl=window.location.origin+'/dashboard';window.location.href='https://auth.emergentagent.com/?redirect='+encodeURIComponent(redirectUrl);}}>{tx("Continuar con Google")}</button></>}</form>{!twoFA.active&&<button className="bc-auth-switch" data-testid="toggle-auth-mode" onClick={()=>{setIsLogin(!isLogin);setError('');navigate(isLogin?'/register':'/login',{replace:true});}}>{isLogin?tx("¿No tienes cuenta? Regístrate"):tx("¿Ya tienes cuenta? Ingresa")}</button>}<footer className="bc-auth-footer">© {new Date().getFullYear()} BRIEFCASE · {tx("Gestión empresarial inteligente")}</footer></div></section></main>;
}
